import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Base/Dialog";
import { Button } from "@/components/Base/Button";
import { Avatar } from "@/components/Common/Avatar";
import {
  ContainerLinkGroup,
  ContainerLinkGroupRow,
  ContainerLinkGroupSync,
} from "@/components/Layout/ContainerLinkGroup";
import { useStore } from "@/stores/useStore";
import { useFeature } from "@/hooks/useFeatures";
import { useBulkLinkEditAvailability } from "@/hooks/useBulkLinkEditAvailability";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import {
  container as containerRepository,
  AddLinksResult,
} from "@/repositories/container";
import { getLinkableLabels } from "@/lib/getLinkableLabels";
import { getIntegrationName } from "@/lib/integration";
import { isTechnicianWritebackEnabledForShop } from "@/lib/technicianWriteback";
import { labelHasCatalogMatch } from "@/lib/labelHasCatalogMatch";
import { sortOptionsByLabel } from "@/lib/sortOptionsByLabel";
import {
  LinkAdditions,
  NO_SYNC_OVERRIDES,
  SyncOverrides,
} from "@/lib/mergeContainerLinks";
import { getEmployeeName } from "@/lib/employees";
import { cn, getInitials } from "@/lib/utils";
import { showErrorToast, showSuccessToast } from "@/lib/toast";
import { IconNames, TerminologyOptions } from "@/types/v2";
import {
  buildLinkRows,
  chooseSync,
  clearSyncChoice,
  getAddOptions,
  LinkKind,
  LinkRow,
  PlannedChange,
  planLinkChanges,
  resolveTargets,
} from "@/lib/bulkLinkPlan";

export interface BulkLinkDialogProps {
  containerIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called once an Apply has settled and the dialog is closing: fully applied,
   * partly applied, or already up to date. Not called when nothing the user
   * chose could be updated (the dialog stays open) or the write threw. `result`
   * separates `changedIds` (gained a link) from `updatedIds` (merely processed).
   */
  onApplied?: (result: AddLinksResult) => void;
}

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

/**
 * Bulk "Edit links": adds and removes technicians and source labels on every
 * container in `containerIds` (WEBV3-340). Existing links can be unlinked from
 * every selected container that holds them (WEBV3-346): a pending removal stays
 * on its row (REMOVING, Undo) until Apply, and Review lists removals first, in
 * red. Two steps: Edit, then a Review that lists every change; nothing is
 * saved until Apply. Per-row
 * two-way sync (WEBV3-341): a Switch on uniform rows, "Mixed n/total" with
 * All on / All off on mixed rows; nothing changes unless the user chooses, and
 * new links start with sync off unless their row is set on. Dispatch is
 * skipped with a notice. Entry points: canvas Bulk Edit (WEBV3-342); list view is WEBV3-337.
 */
export const BulkLinkDialog = ({
  containerIds,
  open,
  onOpenChange,
  onApplied,
}: BulkLinkDialogProps) => {
  const { enabled, showTechnicians, showLabels } =
    useBulkLinkEditAvailability();
  const { shop, employees } = useStore();
  const isROLabelWritebackEnabled = !!useFeature("isROLabelWritebackEnabled");
  const integrationName = getIntegrationName(shop);
  const technicianWritebackEnabled = shop
    ? isTechnicianWritebackEnabledForShop(shop)
    : false;
  // Mirrors ContainerForm: no integration => no sync UI at all; technician
  // sync is shown but disabled while shop writeback is off; label sync is only
  // shown once its rollout flag is on.
  const showTechnicianSync = showTechnicians && !!integrationName;
  const showLabelSync =
    showLabels && !!integrationName && isROLabelWritebackEnabled;
  const { terminolize } = useTerminologyReplacer();

  const [step, setStep] = useState<"edit" | "review">("edit");
  const [pendingTechnicianIds, setPendingTechnicianIds] = useState<string[]>(
    []
  );
  const [pendingLabels, setPendingLabels] = useState<string[]>([]);
  const [pendingRemoveTechnicianIds, setPendingRemoveTechnicianIds] = useState<
    string[]
  >([]);
  const [pendingRemoveLabels, setPendingRemoveLabels] = useState<string[]>([]);
  const [syncChoices, setSyncChoices] =
    useState<SyncOverrides>(NO_SYNC_OVERRIDES);
  const [isApplying, setIsApplying] = useState(false);

  // Reset on any close, including one the parent drives (entry points clear
  // their selection and flip `open` themselves), so a reopen never carries
  // pending adds over from a previous selection.
  useEffect(() => {
    if (!open) {
      setStep("edit");
      setPendingTechnicianIds([]);
      setPendingLabels([]);
      setPendingRemoveTechnicianIds([]);
      setPendingRemoveLabels([]);
      setSyncChoices(NO_SYNC_OVERRIDES);
    }
  }, [open]);

  const allContainers = useMemo(() => shop?.containers ?? [], [shop]);
  const { targets, skippedDispatchCount } = useMemo(
    () => resolveTargets(containerIds, allContainers),
    [containerIds, allContainers]
  );

  const resolveTechnicianName = (id: string) =>
    getEmployeeName(id, employees ?? []);

  const technicianCatalog = useMemo(
    () =>
      sortOptionsByLabel(
        (employees ?? [])
          .filter((emp) => !emp.hideInShopHero)
          .map((emp) => ({
            value: `${emp.id}`,
            label: `${emp.firstName} ${emp.lastName}`,
          }))
      ),
    [employees]
  );
  const labelCatalog = useMemo(
    () =>
      (shop ? getLinkableLabels(shop) : []).map((l) => ({
        value: l,
        label: l,
      })),
    [shop]
  );

  // Only choices the user could actually have made are sent: gated-off
  // sections and shop-writeback-off technicians contribute nothing, so new
  // links there always save with sync off.
  const effectiveSync: SyncOverrides = {
    technicians:
      showTechnicianSync && technicianWritebackEnabled
        ? syncChoices.technicians
        : {},
    labels: showLabelSync ? syncChoices.labels : {},
  };

  // Removal keys are added only when non-empty, so an add-only Apply keeps its
  // exact { technicianIds, labels } payload.
  const pendingChanges: LinkAdditions = {
    technicianIds: showTechnicians ? pendingTechnicianIds : [],
    labels: showLabels ? pendingLabels : [],
    ...(showTechnicians &&
      pendingRemoveTechnicianIds.length > 0 && {
        removeTechnicianIds: pendingRemoveTechnicianIds,
      }),
    ...(showLabels &&
      pendingRemoveLabels.length > 0 && { removeLabels: pendingRemoveLabels }),
  };

  const plan = planLinkChanges(
    targets,
    allContainers,
    pendingChanges,
    resolveTechnicianName,
    effectiveSync
  );

  if (!enabled) return null;

  const handleOpenChange = (next: boolean) => onOpenChange(next);

  const handleApply = async () => {
    setIsApplying(true);
    try {
      const result = await containerRepository.addLinksToContainers(
        targets.map((t) => t.id),
        pendingChanges,
        effectiveSync
      );
      // Toast from the mutation's own counts, and from the containers that
      // actually gained a link (`changedIds`), not every one it processed:
      // Review just told the user "Apply to N containers" for those.
      const processed = result.updatedIds.length;
      const changed = result.changedIds.length;
      // A skipped container only counts as a failure if Review promised it a
      // change: one that already had everything and was then deleted didn't
      // stop anything the user confirmed from landing.
      const promised = new Set(plan.affectedContainerIds);
      const failedSkips = result.skipped.filter((s) => promised.has(s.id));
      const nameById = new Map(
        targets.map((t) => [t.id, t.displayName ?? t.id])
      );
      const failed = failedSkips
        .map((s) => nameById.get(s.id) ?? s.id)
        .join(", ");
      if (processed === 0) {
        showErrorToast(
          "No Containers Updated",
          "None of the selected containers could be updated. They may have been removed. Please try again."
        );
        return;
      }
      if (changed === 0 && failedSkips.length > 0) {
        // Nothing the user confirmed landed (every promised container is
        // gone), even though an unpromised one was processed: same outcome as
        // processed === 0, so stay open with the pending adds intact.
        showErrorToast(
          "No Containers Updated",
          `None of the containers you chose to change could be updated: ${failed}.`
        );
        return;
      }
      if (failedSkips.length > 0) {
        showErrorToast(
          "Some Containers Not Updated",
          `${changed} of ${changed + failedSkips.length} updated. Not updated: ${failed}.`
        );
      } else if (changed === 0) {
        // Another session linked everything since Review: nothing to write.
        showSuccessToast(
          "Already Up to Date",
          "Every selected container was already up to date."
        );
      } else {
        showSuccessToast(
          "Links Updated",
          `Updated ${plural(changed, "container")}.`
        );
      }
      onApplied?.(result);
      handleOpenChange(false);
    } catch (error) {
      console.log(error);
      showErrorToast(
        "Error Updating Links",
        "There was an error updating the selected containers. Please try again."
      );
    } finally {
      setIsApplying(false);
    }
  };

  const buildSync = (row: LinkRow): ContainerLinkGroupSync | undefined => {
    const isTechnician = row.kind === "technician";
    if (!(isTechnician ? showTechnicianSync : showLabelSync)) return undefined;
    const disabled = isTechnician
      ? !technicianWritebackEnabled
      : !labelHasCatalogMatch(row.key, shop?.labelCatalog);
    // Tooltips are ContainerForm's, verbatim.
    const tooltip = isTechnician
      ? technicianWritebackEnabled
        ? `Mark as a technician written back to ${integrationName} on a manual card move`
        : `Turn on technician sync in shop settings to sync back to ${integrationName}`
      : disabled
        ? `No matching ${integrationName} label found — this label can't sync back until it's re-linked to a current ${integrationName} label`
        : `Mark as the label written back to ${integrationName} on a manual card move`;
    const choose = (value: boolean) =>
      setSyncChoices((prev) => chooseSync(prev, row, value));
    return {
      // ContainerForm shows technician rows unchecked while shop writeback is off.
      checked:
        row.syncState === "on" && (!isTechnician || technicianWritebackEnabled),
      disabled,
      onCheckedChange: disabled ? undefined : choose,
      tooltip,
      changed: row.syncChanged,
      onRevert: () =>
        setSyncChoices((prev) => clearSyncChoice(prev, row.kind, row.key)),
      mixed:
        row.syncState === "mixed"
          ? {
              onCount: row.syncOnCount,
              total: row.syncTotal,
              onAllOn: () => choose(true),
              onAllOff: () => choose(false),
            }
          : undefined,
    };
  };

  // Removing a pending link changes the row's counts (its new links vanish).
  // A fully new row (no holders) loses its choice. An extension row (it also
  // has existing links) keeps the user's chosen value, re-evaluated against
  // the post-removal counts so a value equal to the row's stored state clears
  // itself instead of leaving a phantom CHANGED.
  const dropPendingSyncChoice = (row: LinkRow) =>
    setSyncChoices((prev) => {
      const cleared = clearSyncChoice(prev, row.kind, row.key);
      if (row.holderCount === 0 || !row.syncChanged) return cleared;
      return chooseSync(
        cleared,
        { ...row, syncTotal: row.holderCount },
        row.syncState === "on"
      );
    });

  // Marking a link for removal drops any sync choice on the row: the link is
  // going away, so a choice about its sync must not reach Review or Apply.
  const markRemoval = (row: LinkRow) => {
    if (row.kind === "technician") {
      setPendingRemoveTechnicianIds((prev) =>
        Array.from(new Set([...prev, row.key]))
      );
    } else {
      setPendingRemoveLabels((prev) => Array.from(new Set([...prev, row.key])));
    }
    setSyncChoices((prev) => clearSyncChoice(prev, row.kind, row.key));
  };
  const undoRemoval = (row: LinkRow) => {
    if (row.kind === "technician") {
      setPendingRemoveTechnicianIds((prev) =>
        prev.filter((k) => k !== row.key)
      );
    } else {
      setPendingRemoveLabels((prev) => prev.filter((k) => k !== row.key));
    }
  };

  const buildRows = (
    kind: LinkKind,
    pendingKeys: string[],
    removeKeys: string[],
    resolveName: (key: string) => string,
    removePending: (row: LinkRow) => void
  ): ContainerLinkGroupRow[] =>
    buildLinkRows(
      kind,
      targets,
      pendingKeys,
      resolveName,
      effectiveSync,
      removeKeys
    ).map((row) => {
      // A row with a pending add keeps its existing meaning for the button
      // (cancel the add); only an existing-only row unlinks.
      const canUnlink =
        !row.pending && !row.pendingRemove && row.holderCount > 0;
      return {
        key: row.key,
        avatar: (
          <Avatar
            className="h-[26px] w-[26px]"
            fallbackClassName="text-[10px]"
            fallback={getInitials(row.name)}
          />
        ),
        label: row.name,
        subtitle: row.coverage,
        badge: row.pending && row.holderCount === 0 ? "NEW" : undefined,
        onRemove: row.pending
          ? () => removePending(row)
          : canUnlink
            ? () => markRemoval(row)
            : undefined,
        removeTooltip: row.pending
          ? `Remove ${row.name}`
          : canUnlink
            ? `Unlink ${row.name}`
            : undefined,
        removing: row.pendingRemove
          ? {
              onUndo: () => undoRemoval(row),
              undoTooltip: `Undo unlink ${row.name}`,
            }
          : undefined,
        sync: row.pendingRemove ? undefined : buildSync(row),
      };
    });

  const technicianRows = buildRows(
    "technician",
    pendingTechnicianIds,
    pendingRemoveTechnicianIds,
    resolveTechnicianName,
    (row) => {
      setPendingTechnicianIds((prev) => prev.filter((k) => k !== row.key));
      dropPendingSyncChoice(row);
    }
  );
  const labelRows = buildRows(
    "label",
    pendingLabels,
    pendingRemoveLabels,
    (key) => key,
    (row) => {
      setPendingLabels((prev) => prev.filter((k) => k !== row.key));
      dropPendingSyncChoice(row);
    }
  );

  // Removing a link stops its two-way write-back, so say so, but only where
  // sync is actually in effect for that kind (same gates as the sync column).
  const removalSyncNote = (change: PlannedChange): string | undefined => {
    if (!change.isRemoval || !change.syncOnCount || !integrationName) {
      return undefined;
    }
    const syncActive =
      change.kind === "technician"
        ? showTechnicianSync && technicianWritebackEnabled
        : showLabelSync;
    if (!syncActive) return undefined;
    return `Two-way sync is on for ${change.syncOnCount} of these. It stops writing back to ${integrationName}.`;
  };

  const affectedCount = plan.affectedContainerIds.length;
  const hasChanges = plan.changes.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* The base DialogContent sets no height cap. Bulk rows are the union
          across the selection (one container per technician per stage is the
          common shape), so cap to the viewport and scroll the body, keeping
          the header and the Review/Apply footer reachable. */}
      <DialogContent className="flex max-h-screen flex-col">
        <DialogHeader>
          <DialogTitle>
            {step === "edit" ? "Bulk edit links" : "Review changes"}
          </DialogTitle>
          <DialogDescription>
            {step === "edit"
              ? plural(targets.length, "container") + " selected"
              : `${plural(plan.changes.length, "change")} across ${plural(affectedCount, "container")}`}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {step === "edit" ? (
            <div className="flex flex-col gap-5">
              {skippedDispatchCount > 0 && (
                <div className="rounded-md border bg-white px-3 py-2 text-sm text-black">
                  {`${plural(skippedDispatchCount, "container")} skipped: dispatch can't have linked technicians or labels.`}
                </div>
              )}
              {showTechnicians && (
                <div className="flex flex-col gap-2">
                  {showTechnicianSync && !technicianWritebackEnabled && (
                    <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-black">
                      Two-way sync is turned off for this shop. You can still
                      add links; sync controls are disabled.
                    </div>
                  )}
                  <ContainerLinkGroup
                    icon={IconNames.users}
                    title={terminolize(
                      "Linked Technicians",
                      TerminologyOptions.technician,
                      "plural"
                    )}
                    showSyncColumn={showTechnicianSync}
                    rows={technicianRows}
                    emptyText={`No ${terminolize("technicians", TerminologyOptions.technician, "plural").toLowerCase()} linked to this selection yet.`}
                    addOptions={getAddOptions(
                      "technician",
                      targets,
                      technicianCatalog,
                      pendingTechnicianIds,
                      pendingRemoveTechnicianIds
                    )}
                    addPlaceholder={`Add a ${terminolize("technician", TerminologyOptions.technician, "singular").toLowerCase()} to all selected…`}
                    addSearchPlaceholder={`Search ${terminolize("technicians", TerminologyOptions.technician, "plural").toLowerCase()}…`}
                    addEmptyText={`No matching ${terminolize("technician", TerminologyOptions.technician, "singular").toLowerCase()}`}
                    onAddSelect={(value) =>
                      setPendingTechnicianIds((prev) =>
                        Array.from(new Set([...prev, value]))
                      )
                    }
                  />
                </div>
              )}
              {showLabels && (
                <ContainerLinkGroup
                  icon={IconNames.tags}
                  title="Linked Source Labels"
                  showSyncColumn={showLabelSync}
                  rows={labelRows}
                  emptyText="No source labels linked to this selection yet."
                  addOptions={getAddOptions(
                    "label",
                    targets,
                    labelCatalog,
                    pendingLabels,
                    pendingRemoveLabels
                  )}
                  addPlaceholder="Add a source label to all selected…"
                  addSearchPlaceholder="Search source labels…"
                  addEmptyText="No matching source label"
                  onAddSelect={(value) =>
                    setPendingLabels((prev) =>
                      Array.from(new Set([...prev, value]))
                    )
                  }
                />
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {plan.changes.map((change) => {
                const note = removalSyncNote(change);
                return (
                  <div
                    key={`${change.kind}:${change.key}${change.isSync ? ":sync" : ""}${change.isRemoval ? ":unlink" : ""}`}
                    className={cn(
                      "flex flex-col gap-0.5 rounded-md border px-3 py-2",
                      change.isRemoval ? "border-red-300 bg-red-50" : "bg-white"
                    )}
                  >
                    <div className="text-sm font-semibold text-black">
                      {change.headline}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {change.detail}
                    </div>
                    {note && (
                      <div className="text-xs font-semibold text-red-700">
                        {note}
                      </div>
                    )}
                  </div>
                );
              })}
              {plan.conflicts.length > 0 && (
                <div className="flex flex-col gap-1 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-black">
                  <div className="font-semibold">
                    These containers would share the same links as another
                    container
                  </div>
                  <ul className="list-disc pl-5 text-xs">
                    {plan.conflicts.map((conflict) => (
                      <li key={conflict.containerId}>{conflict.message}</li>
                    ))}
                  </ul>
                  <div className="text-xs text-muted-foreground">
                    You can still apply; the container tiles will show a warning
                    afterwards.
                  </div>
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                Links you haven't changed are kept on every container. If some
                containers can't be saved, a message will list them.
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {step === "edit" ? (
            <>
              <div className="mr-auto self-center text-xs text-muted-foreground">
                {hasChanges
                  ? `${plural(plan.changes.length, "pending change")} · ${plural(affectedCount, "container")}`
                  : "No changes yet"}
              </div>
              <Button
                variant="destructive"
                type="button"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={!hasChanges}
                onClick={() => setStep("review")}
              >
                Review changes
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                type="button"
                disabled={isApplying}
                onClick={() => setStep("edit")}
              >
                Back
              </Button>
              <Button
                type="button"
                disabled={!hasChanges || isApplying}
                onClick={handleApply}
              >
                {`Apply to ${plural(affectedCount, "container")}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
